import { useToast } from "@chakra-ui/toast";
import { FormikHelpers } from "formik";
import { FC, useCallback } from "react";
import { graphql, useLazyLoadQuery } from "react-relay";
import { useNavigate } from "react-router-dom";

import {
  UrlMappingDestinationModeType,
  UrlMappingMatchType,
} from "~/src/__generated__/schema";
import { UrlMappingCreateFormContainer_Mutation } from "~/src/__relay_artifacts__/UrlMappingCreateFormContainer_Mutation.graphql";
import { UrlMappingCreateFormContainer_Query } from "~/src/__relay_artifacts__/UrlMappingCreateFormContainer_Query.graphql";
import { useFormErrorHandler } from "~/src/lib/hooks";
import { useMutationCommit } from "~/src/lib/react-relay";

import { UrlMappingForm, UrlMappingFormValues } from "./presentations";
import {
  buildUrlMappingMutationInput,
  emptyConditionsFormValues,
} from "./urlMappingFormUtils";

type Props = {
  siteSlug: string;
};

const query = graphql`
  query UrlMappingCreateFormContainer_Query($siteSlug: String!) {
    site(slug: $siteSlug) {
      owner {
        id
        nextUrlMappingPriority
      }
    }
  }
`;

const mutation = graphql`
  mutation UrlMappingCreateFormContainer_Mutation(
    $input: CreateUrlMappingInput!
  ) {
    createUrlMapping(input: $input) {
      urlMapping {
        id
      }
    }
  }
`;

export const UrlMappingCreateFormContainer: FC<Props> = ({ siteSlug }) => {
  const navigate = useNavigate();
  const toast = useToast();
  const { onFormError } = useFormErrorHandler();

  const { site } = useLazyLoadQuery<UrlMappingCreateFormContainer_Query>(
    query,
    { siteSlug }
  );

  const createMutation =
    useMutationCommit<UrlMappingCreateFormContainer_Mutation>(mutation);

  const initialValues: UrlMappingFormValues = {
    name: "",
    active: true,
    matchType: "EXACT",
    priority: site.owner.nextUrlMappingPriority,
    sourceUrl: "",
    sourceUrls: "",
    sourceHost: "",
    sourcePath: "",
    regexPattern: "",
    destinationUrl: "",
    destinationMode: "STATIC",
    ...emptyConditionsFormValues(),
    startsAt: null,
    endsAt: null,
  };

  const handleSubmit = useCallback(
    async (
      values: UrlMappingFormValues,
      { setErrors }: FormikHelpers<UrlMappingFormValues>
    ) => {
      try {
        const input = buildUrlMappingMutationInput(values);
        const { createUrlMapping } = await createMutation({
          variables: {
            input: {
              siteSlug,
              ...input,
              matchType: input.matchType as UrlMappingMatchType,
              destinationMode:
                input.destinationMode as UrlMappingDestinationModeType,
            },
          },
        });
        if (!createUrlMapping?.urlMapping) throw new Error("assertion failed");

        toast({ title: "URLマッピングを作成しました", status: "success" });
        navigate(`/sites/${siteSlug}/settings?tab=url_mappings`, {
          state: { refetchUrlMappingList: true },
        });
      } catch (err) {
        onFormError(err as any, setErrors);
      }
    },
    [createMutation, navigate, onFormError, siteSlug, toast]
  );

  const handleCancel = useCallback(() => {
    navigate(`/sites/${siteSlug}/settings?tab=url_mappings`);
  }, [navigate, siteSlug]);

  return (
    <UrlMappingForm
      initialValues={initialValues}
      onSubmit={handleSubmit}
      onCancel={handleCancel}
    />
  );
};
